import React, { useEffect, useState } from 'react';
import SessionManager from '../utils/sessionManager';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';

// Build headers with JWT Authorization from SessionManager
const buildAuthHeaders = (extra = {}) => {
  let session = SessionManager.getCurrentSession();
  if (!session || !session.access_token) {
    session = SessionManager.getSession('candidate') || SessionManager.getSession('hr');
  }
  const token = session?.access_token;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const InterviewReportPage = () => {
  const navigate = useNavigate();
  const { interviewId } = useParams();
  const location = useLocation();
  const application = location.state?.application;
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('score'); // score, overall, detailed, transcript
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  useEffect(() => {
    // Check if this is a live interview session
    if (interviewId.startsWith('app-') || interviewId.startsWith('session-')) {
      // Redirect to live interview report page
      navigate(`/hr/live-interview-report/${interviewId}`, { replace: true });
    } else {
      // Regular interview - load report
      loadReport();
    }
  }, [interviewId, navigate]);

  const loadReport = async () => {
    try {
      const response = await fetch(`https://aptirix-version-610696728606.asia-south1.run.app/api/interview/report/${interviewId}`, { headers: buildAuthHeaders() });
      const data = await response.json();
      setReport(data);
      
      // If no scoring yet, trigger scoring
      if (!data.scoring_result) {
        await scoreInterview();
      }
    } catch (err) {
      console.error('Error loading report:', err);
    } finally {
      setLoading(false);
    }
  };

  const scoreInterview = async () => {
    try {
      await fetch(`https://aptirix-version-610696728606.asia-south1.run.app/api/interview/score-interview/${interviewId}`, { headers: buildAuthHeaders(),
        method: 'POST'
      });
      // Reload report after scoring
      setTimeout(() => loadReport(), 2000);
    } catch (err) {
      console.error('Error scoring interview:', err);
    }
  };

  if (loading || !report) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text-light-secondary dark:text-text-dark-secondary">
            Loading interview report...
          </p>
        </div>
      </div>
    );
  }

  const scoring = report.scoring_result || {};
  const overallScore = scoring.overall_percentage || 0;
  const questionScores = scoring.question_scores || [];

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const goodQuestions = questionScores.filter(q => q.score === 1.0).length;
  const avgQuestions = questionScores.filter(q => q.score === 0.5).length;
  const poorQuestions = questionScores.filter(q => q.score === 0.0).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/hr/interviews')}
            >
              <Icon name="arrow_back" size={18} className="mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Candidate Assessment Report</h1>
          </div>
          <Button
            size="md"
            variant="primary"
            onClick={() => setShowScheduleModal(true)}
          >
            <Icon name="event" size={18} className="mr-2" />
            Schedule Meeting
          </Button>
        </div>
      </div>

      {/* Candidate Info & Score */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <div className="flex items-start justify-between">
            {/* Candidate Info */}
            <div className="flex gap-6">
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center">
                <Icon name="person" size={48} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-2 text-gray-900">{application?.candidate_name || 'Unknown Candidate'}</h2>
                <div className="space-y-1 text-gray-600">
                  <p><strong className="text-gray-700">Position:</strong> {report.job_id || 'N/A'}</p>
                  <p><strong className="text-gray-700">Email:</strong> {application?.candidate_email || 'N/A'}</p>
                  <p><strong className="text-gray-700">Phone:</strong> N/A</p>
                  <p><strong className="text-gray-700">Assessment Date:</strong> {report.completed_at ? new Date(report.completed_at).toLocaleString() : 'Invalid Date'}</p>
                </div>
              </div>
            </div>

            {/* Overall Score Circle */}
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-2 font-medium">Overall Score</div>
              <div className="relative w-32 h-32">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    className="text-gray-200"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 56}`}
                    strokeDashoffset={`${2 * Math.PI * 56 * (1 - overallScore / 100)}`}
                    className={overallScore >= 80 ? 'text-green-500' : overallScore >= 50 ? 'text-yellow-500' : 'text-red-500'}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-3xl font-bold text-gray-900">{overallScore}%</div>
                  <div className="text-sm text-gray-500">{scoring.overall_score}/{questionScores.length}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Performance Cards */}
          <div className="grid grid-cols-4 gap-4 mt-8">
            <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4 text-center">
              <div className="text-green-700 text-sm mb-1 font-medium">Good Performance</div>
              <div className="text-3xl font-bold text-gray-900">{goodQuestions}</div>
              <div className="text-sm text-gray-600">Questions (80-100%)</div>
            </div>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4 text-center">
              <div className="text-yellow-700 text-sm mb-1 font-medium">Average Performance</div>
              <div className="text-3xl font-bold text-gray-900">{avgQuestions}</div>
              <div className="text-sm text-gray-600">Questions (40-79%)</div>
            </div>
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 text-center">
              <div className="text-red-700 text-sm mb-1 font-medium">Below Average</div>
              <div className="text-3xl font-bold text-gray-900">{poorQuestions}</div>
              <div className="text-sm text-gray-600">Questions (0-40%)</div>
            </div>
            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 text-center">
              <div className="text-blue-700 text-sm mb-1 font-medium">Total Warnings</div>
              <div className="text-3xl font-bold text-gray-900">{report.total_warnings}</div>
              <div className="text-sm text-gray-600">Proctoring Alerts</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="flex border-b border-gray-200">
            <button
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'score'
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab('score')}
            >
              <Icon name="bar_chart" size={18} className="inline mr-2" />
              Score
            </button>
            <button
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'overall'
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab('overall')}
            >
              <Icon name="description" size={18} className="inline mr-2" />
              Overall Feedback
            </button>
            <button
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'detailed'
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab('detailed')}
            >
              <Icon name="analytics" size={18} className="inline mr-2" />
              Detailed Feedback
            </button>
            <button
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'transcript'
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab('transcript')}
            >
              <Icon name="videocam" size={18} className="inline mr-2" />
              Transcript ({report.questions_and_answers.length})
            </button>
          </div>

          <div className="p-6 bg-gray-50">
            {/* Score Tab */}
            {activeTab === 'score' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold mb-4 text-gray-900">Topic Wise Score</h3>
                  <div className="space-y-4">
                    {Object.entries(scoring.domain_scores || {}).map(([domain, score]) => (
                      <div key={domain} className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex justify-between mb-2">
                          <span className="font-medium uppercase text-sm text-gray-700">{domain}</span>
                          <span className={`font-bold ${getScoreColor(score * 20)}`}>{score.toFixed(1)}/5</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className={`h-2.5 rounded-full ${getScoreBgColor(score * 20)}`}
                            style={{ width: `${(score / 5) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Overall Feedback Tab */}
            {activeTab === 'overall' && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="info" size={20} className="text-blue-600 mt-1" />
                    <p className="text-sm text-gray-700">
                      This detailed analysis is AI-generated based on the candidate's responses, confidence, technical knowledge, and communication skills.
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-bold mb-3 flex items-center gap-2 text-gray-900">
                    <Icon name="description" size={20} className="text-blue-600" />
                    Comprehensive Assessment
                  </h3>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {scoring.comprehensive_feedback || scoring.hr_summary}
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-bold mb-3 flex items-center gap-2 text-gray-900">
                    <Icon name="emoji_events" size={20} className="text-green-600" />
                    Technical Strengths
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {(scoring.technical_strengths || []).map((strength, idx) => (
                      <span key={idx} className="bg-green-100 border border-green-300 text-green-700 px-3 py-1 rounded-full text-sm">
                        {strength}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-bold mb-3 flex items-center gap-2 text-gray-900">
                    <Icon name="trending_up" size={20} className="text-yellow-600" />
                    Areas for Improvement
                  </h3>
                  <ul className="space-y-2">
                    {(scoring.areas_for_improvement || []).map((area, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-700">
                        <Icon name="arrow_right" size={18} className="text-yellow-600 mt-1" />
                        {area}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-bold mb-3 flex items-center gap-2 text-gray-900">
                    <Icon name="visibility" size={20} className="text-purple-600" />
                    Key Observations
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(scoring.key_observations || {}).map(([key, value]) => (
                      <div key={key} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="text-sm text-gray-600 mb-1 capitalize">{key.replace(/_/g, ' ')}</div>
                        <div className="font-semibold text-gray-900">{value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-bold mb-3 text-gray-900">Overall Suitability</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="w-full bg-gray-200 rounded-full h-4">
                        <div
                          className={`h-4 rounded-full ${getScoreBgColor(overallScore)}`}
                          style={{ width: `${overallScore}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{overallScore}/100</div>
                  </div>
                </div>
              </div>
            )}

            {/* Detailed Feedback Tab */}
            {activeTab === 'detailed' && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold mb-4 text-gray-900">Question-wise Performance</h3>
                {questionScores.map((qs, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="bg-gray-100 text-gray-900 px-3 py-1 rounded-full font-bold border border-gray-300">
                          Q{qs.question_number}
                        </span>
                        <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                          {qs.category}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                          qs.score === 1.0 ? 'bg-green-100 text-green-700' :
                          qs.score === 0.5 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {(qs.score * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-red-600">{qs.score.toFixed(1)}/5</div>
                    </div>

                    <div className="mb-4">
                      <p className="text-gray-900 font-medium">{qs.question}</p>
                    </div>

                    <div className="mb-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="text-sm text-gray-600 mb-2 font-medium">Answer:</div>
                      <p className="text-gray-800">{qs.answer}</p>
                    </div>

                    <div className="mb-4 bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <div className="flex items-start gap-2">
                        <Icon name={qs.score >= 0.5 ? 'check_circle' : 'error'} size={20} className={qs.score >= 0.5 ? 'text-green-600' : 'text-red-600'} />
                        <div>
                          <div className="font-semibold mb-1 text-gray-900">Feedback:</div>
                          <p className="text-gray-700">{qs.feedback}</p>
                        </div>
                      </div>
                    </div>

                    {qs.key_points_covered && qs.key_points_covered.length > 0 && (
                      <div className="mb-3 bg-green-50 rounded-lg p-4 border border-green-200">
                        <div className="text-sm font-semibold text-green-700 mb-2">✓ Key Points Covered:</div>
                        <ul className="space-y-1">
                          {qs.key_points_covered.map((point, i) => (
                            <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                              <span className="text-green-600">•</span>
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {qs.missing_points && qs.missing_points.length > 0 && (
                      <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                        <div className="text-sm font-semibold text-yellow-700 mb-2">⚠️ Missing Points:</div>
                        <ul className="space-y-1">
                          {qs.missing_points.map((point, i) => (
                            <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                              <span className="text-yellow-600">•</span>
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Transcript Tab */}
            {activeTab === 'transcript' && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold mb-4 text-gray-900">Interview Transcript with Recordings</h3>
                {report.questions_and_answers.map((qa, idx) => {
                  const score = questionScores.find(qs => qs.question_number === qa.question_number);
                  return (
                    <div key={idx} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <span className="bg-gray-100 text-gray-900 px-3 py-1 rounded-full font-bold border border-gray-300">
                            Q{qa.question_number}
                          </span>
                          <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                            {qa.category}
                          </span>
                          {score && (
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                              score.score === 1.0 ? 'bg-green-100 text-green-700' :
                              score.score === 0.5 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {(score.score * 100).toFixed(0)}%
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="mb-4">
                        <p className="text-gray-900 font-medium">{qa.question_text}</p>
                      </div>

                      {qa.video_url && (
                        <div className="mb-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="text-sm text-gray-700 mb-3 flex items-center gap-2 font-medium">
                            <Icon name="videocam" size={16} className="text-blue-600" />
                            Answer Recording
                          </div>
                          <video
                            controls
                            className="w-full max-w-2xl rounded border border-gray-300"
                            src={qa.video_url.startsWith('http') ? qa.video_url : `https://aptirix-backend-469811621499.asia-south1.run.app/${qa.video_url}`}
                          >
                            Your browser does not support the video tag.
                          </video>
                          <div className="text-sm text-gray-600 mt-2">
                            Duration: {Math.floor(qa.duration_seconds / 60)}:{(qa.duration_seconds % 60).toString().padStart(2, '0')}
                          </div>
                        </div>
                      )}

                      {!qa.video_url && (
                        <div className="mb-4 bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
                          <Icon name="videocam_off" size={32} className="mx-auto mb-2 text-gray-400" />
                          <p className="text-sm text-gray-600">No video recording available for this question</p>
                        </div>
                      )}

                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="text-sm text-gray-700 mb-2 font-medium">Transcript:</div>
                        <p className="text-gray-800">{qa.answer_text || 'No transcript available'}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Schedule Meeting Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Schedule Next Round</h3>
              <button onClick={() => setShowScheduleModal(false)}>
                <Icon name="close" size={24} className="text-gray-400 hover:text-gray-600" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Candidate</label>
                <input
                  type="text"
                  value={application?.candidate_name || ''}
                  disabled
                  className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Type</label>
                <select className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option>Technical Round 2</option>
                  <option>HR Round</option>
                  <option>Final Round</option>
                  <option>Manager Round</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                <input
                  type="time"
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Link</label>
                <input
                  type="url"
                  placeholder="https://meet.google.com/..."
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  rows="3"
                  placeholder="Strong technical skills, good communication..."
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowScheduleModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    alert('Meeting scheduled!');
                    setShowScheduleModal(false);
                  }}
                  className="flex-1"
                >
                  Schedule Meeting
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewReportPage;
